import { parse as Js2XmlParse } from 'js2xmlparser';

// dispatcher and constants
import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { ExportGeometryFormat } from '../constants/ExportGeometryFormats';
import { GenerateImageFormat } from '../constants/GenerateImageFormats';
import { ReadOnly } from '../constants/Enums';

// utils
import { GlobalHelper } from '../utils/GlobalHelper';
import { KompanStore } from '../Kompan/KompanStore';

// Kompan
import { KompanBase, VariantObject, PriceObject } from './KompanBase';

// wcf
import utils = egr.wcf.utils;
import core = egr.wcf.core;
import cf = egr.wcf.cf;
import { ChangedItem, SimpleProperty, getAllArticlesChangedPropertyMap, getArticleChangedPropertyMap } from './PropertyCompare';

export abstract class KompanHelper {
    public static async exportPBox(pLocalPathAndFilename: string): Promise<string> {
        const tBuffer: ArrayBuffer = await KompanHelper.getProjectContainer();
        return KompanHelper.uploadContentToFileStorage(
            utils.arraybuffer.toBase64(tBuffer),
            pLocalPathAndFilename,
            'pbox',
            true
        );
    }

    public static async exportDWG(): Promise<string> {
        // Note: this function needs better error handling
        try {
            // get article
            const tArticle: cf.MainArticleElement = GlobalHelper.coreApp.model.elements[0] as cf.MainArticleElement;
            const tBasketId: string | null = tArticle.basketId;

            if (utils.string.isNullOrEmpty(tBasketId)) {
                return Promise.reject();
            }

            // export geometrie
            const exportTimeout: number = 180000;
            const tExportUrl: string = await GlobalHelper.eaiwsSession.basket.getExportedGeometry(tBasketId, ExportGeometryFormat.DWG_KOMPAN, exportTimeout);

            return (tExportUrl);
        } catch (pError) {
            utils.Log.error('Failed to export geometry.', pError);
            return Promise.reject(pError);
        }
    }

    public static uploadUrlToFileStorage(pUrl: string, pFilename: string): Promise<string> {
        const tExtension: string = pUrl.substr(pUrl.lastIndexOf('.') + 1);
        const tEaiwsBaseUrl: string = GlobalHelper.eaiwsSession.baseUrl;

        return utils.async.ajax(
            'POST',
            tEaiwsBaseUrl + '/EAIWS/plugins/Blacksmith/',
            JSON.stringify({
                input: pUrl,
                tasks: [
                    {
                        name: 'FetchUrlContent'
                    },
                    {
                        name: 'UploadContent',
                        args: {
                            url: tEaiwsBaseUrl + '/EAIWS/plugins/FileStorage/v2/' + pFilename + '.' + tExtension,
                        }
                    }
                ]
            }),
            {
                dataType: 'text',
                headers: { 'Content-Type': 'application/json; charset=utf-8' }
            }).then((result: any): string => {
                return result as string;
            });
    }

    public static uploadContentToFileStorage(pContent: string, pFilename: string, pExtension: string, pIsBinary: boolean): Promise<string> {
        const tEaiwsBaseUrl: string = GlobalHelper.eaiwsSession.baseUrl;
        const tasks: Array<any> = new Array<any>();

        if (pIsBinary) {
            utils.array.addElements(tasks, [
                {
                    name: 'Base64Converter',
                    args: {
                        mode: 'decode'
                    }
                }
            ]);
        }
        utils.array.addElements(tasks, [
            {
                name: 'UploadContent',
                args: {
                    url: tEaiwsBaseUrl + '/EAIWS/plugins/FileStorage/v2/' + pFilename + '.' + pExtension,
                }
            }
        ]);

        return utils.async.ajax(
            'POST',
            tEaiwsBaseUrl + '/EAIWS/plugins/Blacksmith/',
            JSON.stringify({
                input: pContent,
                tasks: tasks
            }),
            {
                dataType: 'text',
                headers: { 'Content-Type': 'application/json; charset=utf-8' }
            }
        ).then((result: any): string => {
            return result as string;
        });
    }

    public static async disableReadOnly(): Promise<void> {
        KompanHelper.storeValue('readOnly', ReadOnly.Disabled);
        KompanHelper.storeValue('variant', null);

        const tElement: core.mdl.SceneElement = GlobalHelper.coreApp.model.elements[0];
        KompanHelper.removeStoredVariantId((tElement as cf.MainArticleElement).basketId);
        tElement.canSelect = (): boolean => { return (true); };
        GlobalHelper.coreApp.model.setSelection([tElement]);
    }

    public static storeValue(pKey: string, pValue: any): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.KOMPAN_SAVE_DATA,
            value: {
                key: pKey,
                payload: pValue
            }
        });
    }

    public static async writeKompanXML(pVariant: VariantObject, pPrice: PriceObject | null): Promise<string> {
        const tData: object = {
            price: pPrice === null ? -1 : pPrice.value,
            paths: {
                dwg: KompanHelper.getWindowsFileName(pVariant, 'dwg'),
                pbox: KompanHelper.getWindowsFileName(pVariant, 'pbox'),
                png: KompanHelper.getWindowsFileName(pVariant, 'png'),
                obx: KompanHelper.getWindowsFileName(pVariant, 'obx')
            },
            NavisionUser: KompanBase.Configuration.NavisionUser,
            PConPathVariants: KompanBase.Configuration.PConPathVariants,
            NavisionSystemName: KompanBase.Configuration.NavisionSystemName,
            SourceSystemConnection: KompanBase.Configuration.SourceSystemConnection,
            PConPathConfigurations: KompanBase.Configuration.PConPathConfigurations,
            PCuser: KompanBase.Configuration.PCuser,
            ProductNo: KompanBase.Configuration.ProductNo,
            Email: KompanBase.Configuration.Email,
            VariantId: pVariant.variantno
        };

        return Js2XmlParse('root', tData);
    }

    public static async getPreviewImage(pElement: cf.ArticleElement): Promise<string> {
        if (pElement.basketId === null) {
            return '';
        }

        return await GlobalHelper.eaiwsSession.basket.getGeneratedImage(pElement.basketId, GenerateImageFormat.Profile);
    }

    public static convertToLocalPath(pNetworkPath: string): string {
        const tPathArray: Array<string> = pNetworkPath.split('\\');
        if (!tPathArray || tPathArray.length === 0) {
            return '';
        }
        return tPathArray.slice(4).join('/');
    }

    public static convertNetworkPathToUrl(pNetworkPath: string): string {
        const tPathArray: Array<string> = pNetworkPath.split('\\');
        if (!tPathArray || tPathArray.length === 0) {
            return '';
        }
        let tFileStorageUrl: string = GlobalHelper.eaiwsSession.baseUrl;
        tFileStorageUrl += '/EAIWS/plugins/FileStorage/v2/';
        return tFileStorageUrl + tPathArray.slice(4).join('/');
    }

    public static getFormattedPrice(pItemId: string|null = null, pTotal: boolean = false): string | null {
        const tPrice: PriceObject | undefined = KompanStore.getState().price as PriceObject;

        if (!tPrice || !tPrice.value) {
            return null;
        }

        const tLocales: string | undefined = KompanBase.Configuration.NumberFormat || undefined;
        const tOptions: Intl.NumberFormatOptions = {};

        if (tPrice.value % 1) {
            tOptions.minimumFractionDigits = 2;
            tOptions.maximumFractionDigits = 2;
        }

        let tLocaleString: string = tPrice.value.toLocaleString(tLocales, tOptions);

        if (tPrice.currency) {
            tLocaleString += ' ' + tPrice.currency;
        }

        return tLocaleString;
    }

    public static async storeVariantId(itemId: string | null | undefined, variantId: string): Promise<void> {
        if (itemId == null) {
            return;
        }
        const appDataString: string = 'variant=' + variantId;
        await GlobalHelper.eaiwsSession.basket.setItemAppData(
            itemId,
            'kompan',
            [appDataString]
        );
    }

    public static removeStoredVariantId(itemId: string | null | undefined): void {
        if (itemId == null) {
            return;
        }
        const appDataString: string = 'variant=';
        void GlobalHelper.eaiwsSession.basket.setItemAppData(
            itemId,
            'kompan',
            [appDataString]
        );
    }

    public static async getProperties(articleElement: cf.ArticleElement, isArticleInsert: boolean): Promise<void> {
        if (utils.string.isNullOrEmpty(articleElement.basketId)) {
            return Promise.resolve();
        }
        const properties: Array<core.prop.Property> | null = await articleElement.getProperties();
        const propertyClasses: Array<core.prop.PropertyClass> | null = await articleElement.getPropertyClasses();
        if (!properties) {
            return Promise.resolve();
        }

        const resultPropertyClasses: Array<core.prop.PropertyClass> = propertyClasses != null ? utils.array.clone(propertyClasses) : [];
        // add default property class
        const tDefaultClass: core.prop.PropertyClass = new core.prop.PropertyClass();
        tDefaultClass.key = '';
        tDefaultClass.name = 'Properties';
        resultPropertyClasses.push(tDefaultClass);
        const resultProperties: Map<string, Array<SimpleProperty>> = new Map();
        for (const prop of properties) {
            let propClass: string | null = prop.class;
            if (propClass == null) {
                propClass = '';
            }

            let simpleProperties: Array<SimpleProperty> | undefined = resultProperties.get(propClass);
            if (simpleProperties == null) {
                simpleProperties = new Array<SimpleProperty>();
                resultProperties.set(propClass, simpleProperties);
            }
            const propValue: string | null | undefined = prop.getValue() !== null ? prop.getValue()!.text : undefined;
            simpleProperties.push({
                name: prop.name,
                value: propValue
            });
        }

        KompanStore.getState().storedFinalProperties.set(articleElement.basketId, resultProperties);
        if (isArticleInsert) {
            KompanStore.getState().storedInitialProperties.set(articleElement.basketId, resultProperties);
        }

        const changedItem: ChangedItem | null = getArticleChangedPropertyMap(articleElement.basketId);
        if (changedItem) {
            const appDataString: string = 'changedProperties=' + JSON.stringify(changedItem.propClasses);
            await GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [appDataString]
            );

            const initialPropertiesAppDataString: string = 'initialProperties=' + JSON.stringify(
                KompanStore.getState().storedInitialProperties.get(changedItem.itemId),
                KompanHelper.jSonMapReplacer
            );
            await GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [initialPropertiesAppDataString]
            );
            const finalPropertiesAppDataString: string = 'finalProperties=' + JSON.stringify(
                KompanStore.getState().storedFinalProperties.get(changedItem.itemId),
                KompanHelper.jSonMapReplacer
            );
            await GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [finalPropertiesAppDataString]
            );
        }
    }

    public static async getChangedProperties(): Promise<void> {
        const changedItems: Array<ChangedItem> = getAllArticlesChangedPropertyMap();

        const promises: Array<Promise<void>> = [];
        for (const changedItem of changedItems) {
            const appDataString: string = 'changedProperties=' + JSON.stringify(changedItem.propClasses);
            promises.push(GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [appDataString]
            ));

            const initialPropertiesAppDataString: string = 'initialProperties=' + JSON.stringify(
                KompanStore.getState().storedInitialProperties.get(changedItem.itemId),
                KompanHelper.jSonMapReplacer
            );
            promises.push(GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [initialPropertiesAppDataString]
            ));
            const finalPropertiesAppDataString: string = 'finalProperties=' + JSON.stringify(
                KompanStore.getState().storedFinalProperties.get(changedItem.itemId),
                KompanHelper.jSonMapReplacer
            );
            promises.push(GlobalHelper.eaiwsSession.basket.setItemAppData(
                changedItem.itemId,
                'kompan',
                [finalPropertiesAppDataString]
            ));
        }
        await Promise.all(promises);
    }

    public static jSonMapReplacer(key: any, value: any): any {
        if (value instanceof Map) {
            return {
                dataType: 'Map',
                value: Array.from(value.entries()),
            };
        }
        return value;
    }

    public static jSonMapReviver(key: any, value: any): any {
        if (typeof value === 'object' && value !== null && value.dataType === 'Map') {
            return new Map(value.value);
        }
        return value;
    }

    private static async getProjectContainer(pEmbedResources: boolean = true): Promise<ArrayBuffer> {
        return GlobalHelper.coreApp.document.saveToZip({
            embedGeometries: pEmbedResources,
            embedTextures: pEmbedResources
        });
    }

    private static getWindowsFileName(pVariant: VariantObject, pExtension: string): string {
        return pVariant.path + pVariant.filename + '.' + pExtension;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Kompan/KompanHelper.ts